import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';

/**
 * StateModal - Generic modal for Energy, Mode, and Motion selection
 * 
 * Props:
 * - title: Modal header text
 * - options: Array of { value, label, icon, color }
 * - selected: Current selection (string for single, array for multi)
 * - multiSelect: boolean
 * - onSave: callback with selection
 * - onClose: callback to close
 */
export default function StateModal({ 
  title, 
  options, 
  selected, 
  multiSelect = false, 
  onSave, 
  onClose 
}) {
  const [selection, setSelection] = useState(() => {
    if (multiSelect) {
      return Array.isArray(selected) ? selected : (selected ? [selected] : []);
    }
    return selected || null;
  });

  const handleOptionClick = (value) => {
    if (multiSelect) {
      const arr = Array.isArray(selection) ? selection : [];
      if (arr.includes(value)) {
        setSelection(arr.filter(v => v !== value));
      } else {
        setSelection([...arr, value]);
      }
    } else {
      // Single select - toggle or set
      setSelection(selection === value ? null : value);
    }
  };

  const handleSave = () => {
    onSave(selection);
  };

  const isSelected = (value) => {
    if (multiSelect) {
      return Array.isArray(selection) && selection.includes(value);
    }
    return selection === value;
  };

  // Allow saving with empty selection (to clear state)
  const canSave = true;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        maxWidth: '360px',
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        left: 'calc(var(--chat-width, 0px) / -2)',
        transition: 'left 0.2s ease'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color={ABLE_COLORS.textMuted} />
          </button>
        </div>

        {/* Options */}
        <div style={{
          padding: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'center'
        }}>
          {options.map((option) => {
            const selected = isSelected(option.value);
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: selected ? option.color : 'transparent',
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusMedium,
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                  boxShadow: selected ? `0 2px 8px ${option.color}40` : 'none',
                  minWidth: multiSelect ? 'auto' : '90px',
                  justifyContent: 'center'
                }}
              >
                {Icon && (
                  <Icon 
                    size={20} 
                    color={selected ? 'white' : ABLE_COLORS.textMuted} 
                  />
                )}
                <span style={{
                  fontSize: '14px',
                  fontWeight: selected ? '600' : '500',
                  color: selected ? 'white' : ABLE_COLORS.textMuted
                }}>
                  {option.label || option.value}
                </span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${ABLE_COLORS.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: ABLE_COLORS.textSecondary
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              padding: '10px 20px',
              background: canSave ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: canSave ? 'pointer' : 'default',
              fontSize: '14px',
              fontWeight: '600',
              color: canSave ? 'white' : ABLE_COLORS.textMuted
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
